// Nadrama <https://nadrama.com>
// Copyright Nadrama Pty Ltd
// All rights reserved.
import { For, Show, createSignal } from "solid-js";
import { createQuery, useQueryClient } from "@tanstack/solid-query";
import type { InvitationBody, MemberBody } from "./api/generated";
import {
  inviteMember,
  listGroups,
  listAccounts,
  listInvitations,
  revokeInvitation,
  updateAccount,
} from "./api/generated";
import { authSlotHeaders } from "./api/runtime";
import { Field, Form, ValidationMessage } from "./components/forms";
import { Heading } from "./components/layout";
import { AccessNotPermitted, useEntitlement } from "./entitlements";
import { formatDate } from "./format";
import {
  accountSettingsFormSchema,
  accountSettingsFormValue,
  invitationFormSchema,
} from "./form-validation";
import { AppLink } from "./navigation";

export function AccountSettings(p: { slot: number; account: string }) {
  const queryClient = useQueryClient();
  const canManage = useEntitlement("account.settings.manage");
  const query = createQuery(() => ({
    queryKey: [p.slot, p.account, "account"],
    queryFn: () =>
      listAccounts({
        throwOnError: true,
        responseStyle: "fields",
        headers: authSlotHeaders(p.slot),
      }),
  }));
  const account = () =>
    query.data?.data.items?.find((item) => item.id === p.account);
  return (
    <>
      <Heading
        title="Account settings"
        copy="Manage your account details and notification recipients."
      />
      <Show when={canManage()} fallback={<AccessNotPermitted />}>
        <section class="panel p-6">
          <Show when={query.isPending}>
            <p>Loading account settings…</p>
          </Show>
          <Show when={query.isError}>
            <div role="alert" class="alert alert-error">
              Unable to load account settings. Please try again.
            </div>
          </Show>
          <Show when={account()} keyed>
            {(current) => (
              <Form<ReturnType<typeof accountSettingsFormValue>>
                schema={accountSettingsFormSchema}
                value={accountSettingsFormValue}
                action={async (d) => {
                  await updateAccount({
                    throwOnError: true,
                    responseStyle: "fields",
                    path: { account: p.account },
                    headers: authSlotHeaders(p.slot),
                    body: d,
                  });
                  await queryClient.invalidateQueries({ queryKey: [p.slot] });
                }}
              >
              <Field
                name="name"
                label="Account name"
                value={current.name}
                required
                minLength={1}
                maxLength={255}
              />
              <Field
                name="organisation"
                label="Organisation/Company Name (optional)"
                value={current.organisation}
                maxLength={255}
              />
              <Field
                name="country"
                label="Country"
                value={current.country}
                required
                minLength={2}
                maxLength={2}
                pattern="[A-Za-z]{2}"
              />
              <Field
                name="notification_emails"
                type="email"
                value={current.notificationEmails}
                label="Notification emails"
                help="Comma-separated operational recipients. Addresses are normalized and duplicates removed."
                required
                multiple
              />
              </Form>
            )}
          </Show>
        </section>
      </Show>
    </>
  );
}
export function MembersPage(p: {
  slot: number;
  account: string;
  members: MemberBody[];
}) {
  const queryClient = useQueryClient();
  const [revoking, setRevoking] = createSignal("");
  const canViewInvitations = useEntitlement("account.invitation.view");
  const canManageInvitations = useEntitlement("account.invitation.manage");
  const canManageGroups = useEntitlement("account.group.manage");
  const invitations = createQuery(() => ({
    queryKey: [p.slot, p.account, "invitations"],
    queryFn: () =>
      listInvitations({
        throwOnError: true,
        responseStyle: "fields",
        path: { account: p.account },
        headers: authSlotHeaders(p.slot),
      }),
    enabled: canViewInvitations(),
  }));
  const groups = createQuery(() => ({
    queryKey: [p.slot, p.account, "groups"],
    queryFn: async () =>
      (await listGroups({ throwOnError: true, responseStyle: "fields", path: { account: p.account }, headers: authSlotHeaders(p.slot) })).data.items ?? [],
    enabled: canManageInvitations(),
  }));
  const pending = () => invitations.data?.data.items ?? [];
  const roleName = (role: string) => (role === "admin" ? "Admin" : "Member");
  const revoke = async (invitation: InvitationBody) => {
    if (!confirm(`Revoke the invitation for ${invitation.email}?`)) return;
    setRevoking(invitation.id);
    try {
      await revokeInvitation({
        throwOnError: true,
        responseStyle: "fields",
        path: { account: p.account, id: invitation.id },
        headers: authSlotHeaders(p.slot),
      });
      await queryClient.invalidateQueries({
        queryKey: [p.slot, p.account, "invitations"],
        exact: true,
      });
    } finally {
      setRevoking("");
    }
  };
  return (
    <>
      <Heading
        title="Members"
        copy="View account members and invite people to join a group."
      />
      <section class="grid gap-4">
        <h2 class="text-xl font-bold">Account members</h2>
        <div class="desktop-table panel overflow-x-auto">
          <table class="table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Group</th>
                <th>Group role</th>
              </tr>
            </thead>
            <tbody>
              <For each={p.members}>
                {(member) => (
                  <tr>
                    <td>{member.email}</td>
                    <td>
                      <Show when={canManageGroups()} fallback={member.groupName}>
                        <AppLink
                          class="font-medium text-primary hover:underline"
                          to={`/u${p.slot}/${p.account}/groups/${member.groupId}/edit`}
                        >
                          {member.groupName}
                        </AppLink>
                      </Show>
                    </td>
                    <td>{roleName(member.groupRole)}</td>
                  </tr>
                )}
              </For>
            </tbody>
          </table>
        </div>
        <div class="mobile-card gap-3">
          <For each={p.members}>
            {(member) => (
              <article class="panel p-4">
                <h3 class="font-bold">{member.email}</h3>
                <p class="mt-2 text-sm">
                  <Show when={canManageGroups()} fallback={member.groupName}>
                    <AppLink
                      class="font-medium text-primary hover:underline"
                      to={`/u${p.slot}/${p.account}/groups/${member.groupId}/edit`}
                    >
                      {member.groupName}
                    </AppLink>
                  </Show>{" "}
                  · {roleName(member.groupRole)}
                </p>
              </article>
            )}
          </For>
        </div>
      </section>
      <section class="mt-8 grid gap-4">
        <h2 class="text-xl font-bold">Pending invitations</h2>
        <Show
          when={canViewInvitations()}
          fallback={
            <AccessNotPermitted>
              You don’t have permission to view pending invitations. Ask an
              account administrator if you need access.
            </AccessNotPermitted>
          }
        >
          <Show when={invitations.isPending}>
            <p>Loading pending invitations…</p>
          </Show>
          <Show when={invitations.isError}>
            <div role="alert" class="alert alert-error">
              Unable to load pending invitations.
            </div>
          </Show>
          <Show when={invitations.isSuccess}>
            <Show
              when={pending().length}
              fallback={
                <>
                  <hr class="border-slate-200" />
                  <p class="py-6 text-slate-400">No pending invitations.</p>
                </>
              }
            >
            <div class="desktop-table panel overflow-x-auto">
              <table class="table">
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Group</th>
                    <th>Group role</th>
                    <th>Expires</th>
                    <th class="text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <For each={pending()}>
                    {(invitation) => (
                      <tr>
                        <td>{invitation.email}</td>
                        <td>{invitation.groupName}</td>
                        <td>{roleName(invitation.groupRole)}</td>
                        <td>{formatDate(invitation.expiresAt)}</td>
                        <td class="text-right">
                          <Show when={canManageInvitations()}>
                            <button
                              class="btn btn-ghost btn-sm text-error"
                              disabled={revoking() === invitation.id}
                              onClick={() => revoke(invitation)}
                            >
                              {revoking() === invitation.id
                                ? "Revoking…"
                                : "Revoke"}
                            </button>
                          </Show>
                        </td>
                      </tr>
                    )}
                  </For>
                </tbody>
              </table>
            </div>
            <div class="mobile-card gap-3">
              <For each={pending()}>
                {(invitation) => (
                  <article class="panel p-4">
                    <h3 class="font-bold">{invitation.email}</h3>
                    <p class="mt-2 text-sm">
                      {invitation.groupName} · {roleName(invitation.groupRole)} · expires{" "}
                      {formatDate(invitation.expiresAt)}
                    </p>
                    <Show when={canManageInvitations()}>
                      <button
                        class="btn btn-ghost btn-sm mt-3 text-error"
                        disabled={revoking() === invitation.id}
                        onClick={() => revoke(invitation)}
                      >
                        {revoking() === invitation.id ? "Revoking…" : "Revoke"}
                      </button>
                    </Show>
                  </article>
                )}
              </For>
            </div>
            </Show>
          </Show>
        </Show>
      </section>
      <Show when={canManageInvitations()}>
        <section class="panel mt-8 p-6">
          <h2 class="mb-4 text-xl font-bold">Invite a member</h2>
          <Form
          schema={invitationFormSchema}
          submitLabel="Send Invite"
          successMessage="Invite sent!"
          resetOnSuccess
          action={async (d) => {
            await inviteMember({
              throwOnError: true,
              responseStyle: "fields",
              path: { account: p.account },
              headers: authSlotHeaders(p.slot),
              body: { email: d.email ?? "", groupId: d.groupId ?? "" },
            });
            await queryClient.invalidateQueries({
              queryKey: [p.slot, p.account, "invitations"],
              exact: true,
            });
          }}
        >
          <Field
            name="email"
            type="email"
            label="Email address"
            placeholder="Email to invite..."
            required
          />
          <div class="field">
            <label for="invite-group">Group</label>
            <select
              class="select select-bordered w-full"
              id="invite-group"
              name="groupId"
              required
            >
              <option value="" disabled selected>Select a group...</option>
              <For each={groups.data}>{(group) => <option value={group.id}>{group.name} ({roleName(group.role)})</option>}</For>
            </select>
            <ValidationMessage name="groupId" />
          </div>
          </Form>
        </section>
      </Show>
    </>
  );
}
